import { Link } from "@tanstack/react-router";

/**
 * Long-form, locally-keyworded content block rendered near the bottom of the
 * homepage. Targets phrase-match for "Colorado Springs digital marketing
 * agency", "Colorado Springs marketing agency", "digital marketing services
 * Colorado Springs", "Colorado Springs internet marketing", and
 * "marketing company / firm Colorado Springs" without keyword stuffing.
 */
export function ColoradoSpringsMarketingSEO() {
  return (
    <section className="bg-background py-20 sm:py-24">
      <div className="mx-auto max-w-4xl px-5 lg:px-8">
        <span className="inline-flex items-center gap-2 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-gold">
          Colorado Springs
        </span>
        <h2 className="mt-5 text-3xl font-black leading-tight text-foreground sm:text-4xl lg:text-5xl">
          Colorado Springs digital marketing agency{" "}
          <span className="text-gradient-gold">built for local results</span>
        </h2>
        <p className="mt-5 text-lg leading-relaxed text-muted-foreground">
          Colorado Web Impressions is a Colorado Springs digital marketing
          agency working with local trades, professional services, and
          retailers from Briargate to Broadmoor, Falcon to Fountain. Owner-led
          since 2006 — every campaign is built and managed in-house from our
          office on E. Woodmen Rd.
        </p>

        <div className="mt-12 space-y-10">
          <div>
            <h3 className="text-2xl font-bold text-foreground">
              What a Colorado Springs marketing agency actually does
            </h3>
            <p className="mt-3 leading-relaxed text-muted-foreground">
              The job of a real marketing agency in Colorado Springs is to
              earn placement in the four places local buyers actually look:
              the Google Map Pack, organic search results, AI answers
              (ChatGPT, Perplexity, Google AI Overviews), and paid ads. We
              build websites that convert that traffic into booked calls — no
              templates, no outsourcing, no long-term contracts.
            </p>
          </div>

          <div>
            <h3 className="text-2xl font-bold text-foreground">
              Digital marketing services for Colorado Springs businesses
            </h3>
            <ul className="mt-4 space-y-2 leading-relaxed text-muted-foreground">
              <li>
                <Link to="/colorado-springs-seo" className="font-semibold text-brand-gold hover:underline">
                  Colorado Springs SEO
                </Link>{" "}
                — Map Pack rankings, on-page depth, and neighborhood content
                across El Paso County.
              </li>
              <li>
                <Link to="/colorado-springs-web-design" className="font-semibold text-brand-gold hover:underline">
                  Colorado Springs web design
                </Link>{" "}
                — hand-coded, mobile-first sites engineered to convert local
                traffic into leads.
              </li>
              <li>
                <Link to="/colorado-springs-google-maps" className="font-semibold text-brand-gold hover:underline">
                  Google Maps marketing
                </Link>{" "}
                — GBP optimization, citation cleanup, and review programs that
                move you into the 3-pack.
              </li>
              <li>
                <Link to="/google-ads-ppc" className="font-semibold text-brand-gold hover:underline">
                  Google Ads &amp; PPC
                </Link>{" "}
                — tight, ROI-tracked campaigns for Colorado Springs service
                areas and PCS-cycle demand.
              </li>
              <li>
                <Link to="/geo-ai" className="font-semibold text-brand-gold hover:underline">
                  GEO / AI search optimization
                </Link>{" "}
                — get cited when Colorado Springs buyers ask ChatGPT,
                Perplexity, and Gemini.
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-2xl font-bold text-foreground">
              Why local matters for marketing in Colorado Springs
            </h3>
            <p className="mt-3 leading-relaxed text-muted-foreground">
              Colorado Springs isn't Denver. Buyers here Google by
              neighborhood — Briargate, Powers, Old Colorado City, Black
              Forest, Monument, Falcon — and the Map Pack is dominated by
              shops that look unmistakably local. PCS rotations through
              Peterson, Schriever, and Fort Carson drive a constant flow of
              new-mover demand, and the Front Range has a steady stream of
              Denver agencies trying to claim it from 70 miles north. A
              Colorado Springs marketing firm that lives here, works here,
              and answers the phone here wins those buyers — every time.{" "}
              <Link to="/colorado-springs" className="font-semibold text-brand-gold hover:underline">
                See our full Colorado Springs marketing hub →
              </Link>
            </p>
          </div>
        </div>

        <p className="mt-12 border-t border-border pt-8 text-base leading-relaxed text-muted-foreground">
          Whether you're searching for a digital marketing company in Colorado
          Springs, a Colorado Springs internet marketing partner, or just a
          straight-shooting marketing firm in Colorado Springs that won't lock
          you into a 12-month contract — start with a free 30-minute strategy
          call. We'll audit your current site, GBP, and top 3 competitors,
          then tell you exactly what to fix first.
        </p>
      </div>
    </section>
  );
}
